import { useMutation, useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import DriveFileMove from "@mui/icons-material/DriveFileMove";
import DriveFileMoveOutlined from "@mui/icons-material/DriveFileMoveOutlined";
import Edit from "@mui/icons-material/Edit";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridValidRowModel } from "@mui/x-data-grid-premium";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid, IssueMessageCell } from "components/DataGrid";
import {
  AddEditDocumentDialogState,
  DocumentDialog,
  EditMode,
  SimilarityCell,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { FormState } from "components/Document/AddEditDocumentDialog/types";
import { DocumentDetailDrawer } from "components/Document/DocumentDetailDrawer";
import { FileTypeIcon } from "components/Document/shared/fileTypeUtils";
import { DownloadAllButton } from "components/DownloadAllButton";
import { FacilityReadOnlyBanner } from "components/FacilityReadOnlyBanner";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { KnownSubmissionDocumentTypes } from "encamp-shared/src/documents";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { gql } from "generated-graphql";
import {
  DocumentGridRowFragment,
  DocumentType,
  Issue,
  IssueLevel,
  OtherFacilityDocumentsQuery,
  OtherFacilityDocumentsQueryVariables,
  TierIiReportEncampStatus,
} from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentAI } from "hooks/useDocumentAI";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { reportKindToLabel, statusToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

import { useIsCalifornia } from "./Report/useHmbp";

const MAX_VISIBLE_YEARS = 3;

const isDefaultSubmissionDoc = (doc: {
  facilityDocument?: { currentSubmissionDocument: boolean } | null;
  documentType?: string | null;
}) =>
  doc.facilityDocument?.currentSubmissionDocument === true &&
  KnownSubmissionDocumentTypes.includes(doc.documentType as DocumentType);

const CHEMICAL_FACILITY_DOCUMENTS = gql(`
  query ChemicalFacilityDocuments($facilityId: ID!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facility(id: $facilityId) {
      id
      name
      state
      documents {
        id
        documentType
        storageLink
        facilityDocument {
          id
          currentSubmissionDocument
        }
        sizeMegabytes
      }
    }
    documents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        ...DocumentGridRow
      }
    }
  }
`);

const FACILITY_REPORTS_LIST = gql(`
  query FacilityReportsList($facilityId: ID!) {
    facilityReportsList(facilityId: $facilityId) {
      id
      reportingYear
      reportKind
      organizationStatus
      encampStatus
      submittedAt
      verifiedAt
      verifierFullName
    }
  }
`);

const REPORT_ALL_DOCUMENTS = gql(`
  query ReportAllDocumentsForFacility($reportId: ID!, $sort: [SortModel!]) {
    tierIIReportAllDocuments(reportId: $reportId, sort: $sort) {
      ...DocumentGridRow
    }
  }
`);

const REPORT_DOCUMENT_ISSUES = gql(`
  query ReportDocumentIssues($reportId: ID!) {
    tierIIReportDocumentIssues(reportId: $reportId) {
      ...issue
    }
  }
`);

const OTHER_FACILITY_DOCUMENTS = gql(`
  query OtherFacilityDocuments($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    documents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        ...DocumentGridRow
        facilities {
          id
          name
        }
      }
      similarities {
        documentId
        similarity
      }
    }
  }
`);

const SET_CURRENT_SUBMISSION_DOCUMENT = gql(`
  mutation SetCurrentSubmissionDocument($id: ID, $document: DocumentInput2!, $associations: [DocumentAssociationsInput!]!, $currentSubmissionDocument: Boolean!) {
    upsertDocument(id: $id, document: $document, associations: $associations, currentSubmissionDocument: $currentSubmissionDocument) {
      id
    }
  }
`);

type DocumentIssueRow = Issue & GridValidRowModel;
type DataGridRow = DocumentGridRowFragment | DocumentIssueRow;

const isDocumentIssueRow = (row: DataGridRow): row is DocumentIssueRow => {
  return (row as DocumentIssueRow).key !== undefined;
};

export function ChemicalFacilityDocuments() {
  const { tenantId, facilityId } = useParams();
  const { isStaff, user } = useCurrentUser();
  const { isReadOnly } = useFacilityReadOnly(facilityId);
  const hasDocumentAI = useDocumentAI();
  const tenantName = user?.UserTenant.find((ut) => ut.tenantId === tenantId)?.tenant?.name;
  const theme = useTheme();
  const [selectedReportId, setSelectedReportId] = useState<string>("");

  // Track similarity scores from semantic search results
  const similarityMapRef = useRef<Map<string, number> | null>(null);
  const [hasSemanticResults, setHasSemanticResults] = useState(false);

  const handleOtherDocsDataStateChange = useCallback(
    (data: OtherFacilityDocumentsQuery | undefined) => {
      const similarities = data?.documents?.similarities;
      if (similarities && similarities.length > 0) {
        similarityMapRef.current = new Map(similarities.map((s) => [s.documentId, s.similarity]));
        setHasSemanticResults(true);
      } else {
        similarityMapRef.current = null;
        setHasSemanticResults(false);
      }
    },
    []
  );

  const { data, loading } = useQuery(CHEMICAL_FACILITY_DOCUMENTS, {
    variables: {
      facilityId: facilityId ?? "",
      search: `tenantId:${tenantId} facilityId:${facilityId} excludeRejectedSubmissionDocuments:true`,
      page: 0,
      pageSize: 200,
    },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });

  const facilityState = data?.facility?.state;
  const isCaliforniaFacility = useIsCalifornia(facilityState ?? "");

  const { data: reportsData } = useQuery(FACILITY_REPORTS_LIST, {
    variables: { facilityId: facilityId ?? "" },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });

  const reports = useMemo(
    () => reportsData?.facilityReportsList ?? [],
    [reportsData?.facilityReportsList]
  );

  useEffect(() => {
    if (reports.length > 0 && !selectedReportId) {
      setSelectedReportId(reports[0].id);
    }
  }, [reports, selectedReportId]);

  const { data: reportDocsData, loading: reportDocsLoading } = useQuery(REPORT_ALL_DOCUMENTS, {
    variables: {
      reportId: selectedReportId,
    },
    skip: !selectedReportId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const { data: issuesData } = useQuery(REPORT_DOCUMENT_ISSUES, {
    variables: { reportId: selectedReportId },
    skip: !selectedReportId,
    fetchPolicy: "cache-and-network",
  });

  const [setCurrentSubmission] = useMutation(SET_CURRENT_SUBMISSION_DOCUMENT, {
    refetchQueries: [CHEMICAL_FACILITY_DOCUMENTS, REPORT_ALL_DOCUMENTS, REPORT_DOCUMENT_ISSUES],
  });

  const { fetchAndHandleDownload } = useDocumentDownload();
  const [deleteDocumentMutation] = useDeleteDocumentMutation({
    refetchQueries: [CHEMICAL_FACILITY_DOCUMENTS],
  });

  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [documentToRemove, setDocumentToRemove] = useState<DocumentGridRowFragment | undefined>();
  const [drawerDocument, setDrawerDocument] = useState<DocumentGridRowFragment | undefined>();
  const [fixCandidate, setFixCandidate] = useState<{
    document: DocumentGridRowFragment;
    issue: Issue;
    issueType: "error" | "warning";
    action: "attach" | "upload" | "set-flag";
    description: string;
  } | null>(null);

  const allDocuments = useMemo(() => data?.documents.items ?? [], [data?.documents.items]);

  const reportDocuments = useMemo(
    () => reportDocsData?.tierIIReportAllDocuments ?? [],
    [reportDocsData]
  );

  const reportDocumentIssues = useMemo(
    () => issuesData?.tierIIReportDocumentIssues ?? [],
    [issuesData]
  );

  const reportDocIds = useMemo(() => new Set(reportDocuments.map((d) => d.id)), [reportDocuments]);

  const existingDefaults = useMemo(
    () =>
      allDocuments
        .filter((d) => isDefaultSubmissionDoc(d))
        .map((d) => ({
          id: d.id,
          documentType: d.documentType,
          title: d.title,
        })),
    [allDocuments]
  );

  const selectedReport = reports.find((r) => r.id === selectedReportId);

  const isReportFilingComplete =
    selectedReport?.encampStatus === TierIiReportEncampStatus.FilingComplete;

  const STAFF_WARNING_KEY = "staff-currentSubmissionDocument";
  const STAFF_DUPLICATE_WARNING_KEY = "staff-duplicateCurrentSubmissionDocument";

  const staffWarnings = useMemo(() => {
    if (!isStaff) return [];
    const warnings: DocumentIssueRow[] = [];
    for (const docType of KnownSubmissionDocumentTypes) {
      const onReport = reportDocuments.some((d) => d.documentType === docType);
      if (!onReport) continue;

      const currentSubmissionDocs = allDocuments.filter(
        (d) => d.documentType === docType && d.facilityDocument?.currentSubmissionDocument === true
      );

      if (currentSubmissionDocs.length === 0) {
        const label = prettyPrintDocumentType(docType as unknown as DocumentType);
        warnings.push({
          id: `staff-warning-${docType}`,
          level: IssueLevel.Error,
          message: `${label} is not set as a Current Submission Document and will not be copied to future reports.`,
          modelName: "FacilityDocument",
          key: STAFF_WARNING_KEY,
          metadata: { documentType: docType },
        });
      } else if (currentSubmissionDocs.length > 1 && !isCaliforniaFacility) {
        // California (CERS) allows multiple current submission documents per
        // type, so multiple current docs is only an anomaly for non-California
        // facilities (e.g. legacy data that predates per-type exclusivity).
        const label = prettyPrintDocumentType(docType as unknown as DocumentType);
        warnings.push({
          id: `staff-warning-dup-${docType}`,
          level: IssueLevel.Error,
          message: `Multiple ${label} documents are set as a Current Submission Document and all of them will be copied to future reports.`,
          modelName: "FacilityDocument",
          key: STAFF_DUPLICATE_WARNING_KEY,
          metadata: { documentType: docType },
        });
      }
    }
    return warnings;
  }, [isStaff, allDocuments, reportDocuments, isCaliforniaFacility]);

  const fixCandidatesByIssue = useMemo(() => {
    const map = new Map<
      string,
      {
        document: DocumentGridRowFragment;
        issueType: "error" | "warning";
        action: "attach" | "upload" | "set-flag";
        description: string;
      }
    >();

    for (const issue of reportDocumentIssues) {
      const issueDocType = issue.metadata?.documentType as string | undefined;
      if (!issueDocType) continue;

      const fromFacility = allDocuments.find(
        (d) => d.documentType === issueDocType && !reportDocIds.has(d.id)
      );
      if (fromFacility && !isReportFilingComplete) {
        map.set(issue.message, {
          document: fromFacility,
          issueType: "error",
          action: "attach",
          description: `We found "${fromFacility.title}" in this facility's documents.`,
        });
      }
    }

    for (const warning of staffWarnings) {
      if (warning.key !== STAFF_WARNING_KEY) continue;
      const warnDocType = warning.metadata?.documentType as string | undefined;
      if (!warnDocType) continue;

      const onReport = reportDocuments.find((d) => d.documentType === warnDocType);
      if (onReport) {
        map.set(warning.message, {
          document: onReport,
          issueType: "warning",
          action: "set-flag",
          description: `"${onReport.title}" is on this report but is not set as a Current Submission Document. Set it now so it will be copied to future reports?`,
        });
      }
    }

    return map;
  }, [
    reportDocumentIssues,
    staffWarnings,
    allDocuments,
    reportDocuments,
    reportDocIds,
    isReportFilingComplete,
  ]);

  const handleFixAttach = async () => {
    if (!fixCandidate) return;
    const doc = fixCandidate.document;
    const associations: Array<{
      tenantId?: string;
      facilityId?: string;
      reportId?: string;
    }> = [
      {
        tenantId: tenantId ?? "",
        facilityId: facilityId ?? "",
      },
    ];

    if (selectedReportId) {
      associations.push({ reportId: selectedReportId });
    }

    await setCurrentSubmission({
      variables: {
        id: doc.id,
        document: {
          title: doc.title,
          documentType: doc.documentType,
          storageLink: doc.storageLink,
        },
        associations,
        currentSubmissionDocument: true,
      },
    });
    setFixCandidate(null);
  };

  const handleFixUpload = () => {
    if (!fixCandidate) return;
    const issueDocType = fixCandidate.issue.metadata?.documentType as string | undefined;
    setAddEditDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.ADD,
      documentType: (issueDocType as DocumentType) ?? undefined,
      currentSubmissionDocument: true,
      facilities: data?.facility ? [{ id: data.facility.id, name: data.facility.name }] : undefined,
    });
    setFixCandidate(null);
  };

  const handleFixSetFlag = async () => {
    if (!fixCandidate) return;
    const doc = fixCandidate.document;
    await setCurrentSubmission({
      variables: {
        id: doc.id,
        document: {
          title: doc.title,
          documentType: doc.documentType,
          storageLink: doc.storageLink,
        },
        associations: [
          {
            tenantId: tenantId ?? "",
            facilityId: facilityId ?? "",
          },
        ],
        currentSubmissionDocument: true,
      },
    });
    setFixCandidate(null);
  };

  useBreadcrumb([
    { label: "Chemicals", to: `/o/${tenantId}/chemicals` },
    { label: "Documents", to: `/o/${tenantId}/chemicals/documents` },
    { label: data?.facility?.name ?? "" },
  ]);

  const handleSetCurrent = async (doc: DocumentGridRowFragment, isCurrent: boolean) => {
    await setCurrentSubmission({
      variables: {
        id: doc.id,
        document: {
          title: doc.title,
          documentType: doc.documentType,
          storageLink: doc.storageLink,
        },
        associations: [
          {
            tenantId: tenantId ?? "",
            facilityId: facilityId ?? "",
          },
        ],
        currentSubmissionDocument: isCurrent,
      },
    });
  };

  const openAddDialog = () => {
    setAddEditDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.ADD,
      currentSubmissionDocument: true,
      facilities: data?.facility ? [{ id: data.facility.id, name: data.facility.name }] : undefined,
    });
  };

  const openEditDialog = (doc: DocumentGridRowFragment) => {
    setAddEditDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.EDIT,
      step: FormState.FORM,
      documentId: doc.id,
      title: doc.title,
      documentType: doc.documentType as DocumentType,
      storageLink: doc.storageLink ?? undefined,
      fileExtension: doc.fileExtension ?? undefined,
      description: doc.description ?? undefined,
      authoredAt: doc.authoredAt ?? undefined,
      currentSubmissionDocument: doc.facilityDocument?.currentSubmissionDocument ?? false,
      facilities: data?.facility ? [{ id: data.facility.id, name: data.facility.name }] : undefined,
    });
  };

  const reportingYearsColumn: GridColDef<DataGridRow> = {
    field: "associatedReports",
    headerName: "Reporting Years",
    width: 220,
    sortable: false,
    renderCell({ row }) {
      if (isDocumentIssueRow(row)) return null;
      const reports = row.associatedReports ?? [];
      const epcraYears = [
        ...new Set(reports.filter((r) => r.programArea === "EPCRA").map((r) => r.reportingYear)),
      ].sort((a, b) => b - a);

      if (epcraYears.length === 0) return null;

      const visible = epcraYears.slice(0, MAX_VISIBLE_YEARS);
      const overflow = epcraYears.length - MAX_VISIBLE_YEARS;

      return (
        <Stack direction="row" flexWrap="nowrap" gap={0.5} overflow="hidden">
          {visible.map((year) => (
            <Chip key={year} label={year} size="small" color="primary" variant="outlined" />
          ))}
          {overflow > 0 && (
            <Tooltip title={epcraYears.slice(MAX_VISIBLE_YEARS).join(", ")}>
              <Chip label={`+${overflow}`} size="small" variant="outlined" />
            </Tooltip>
          )}
        </Stack>
      );
    },
  };

  const currentSubmissionTooltip = (
    <span>
      <strong>Current submission document</strong>
      <br />
      This document will be automatically attached to future reports
    </span>
  );

  const buildColumns = (section: "report" | "other"): GridColDef<DataGridRow>[] => [
    {
      field: "documentType",
      headerName: "Type",
      width: 300,
      colSpan: (_, row) => {
        if (isDocumentIssueRow(row)) return 5;
        return undefined;
      },
      renderCell({ row }) {
        if (isDocumentIssueRow(row)) {
          const candidate = fixCandidatesByIssue.get(row.message);
          const isStaffWarning =
            row.key === STAFF_WARNING_KEY || row.key === STAFF_DUPLICATE_WARNING_KEY;
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              {isStaffWarning ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <WarningAmberOutlined
                    fontSize="small"
                    sx={{ color: theme.palette.secondary.main, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                    variant="body2"
                  >
                    <strong>
                      {prettyPrintDocumentType(row.metadata?.documentType as DocumentType)}
                    </strong>{" "}
                    {row.key === STAFF_DUPLICATE_WARNING_KEY
                      ? "has multiple documents set as Current Submission Document. All of them will be copied to future reports."
                      : "is not set as a Current Submission Document and will not be copied to future reports."}
                  </Typography>
                </Stack>
              ) : (
                <IssueMessageCell issue={row} />
              )}
              {candidate && !isReadOnly && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFixCandidate({
                      document: candidate.document,
                      issue: row,
                      issueType: candidate.issueType,
                      action: candidate.action,
                      description: candidate.description,
                    });
                  }}
                  sx={{ whiteSpace: "nowrap", ml: 1 }}
                >
                  Fix
                </Button>
              )}
              {!candidate && !isStaffWarning && !isReadOnly && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const issueDocType = row.metadata?.documentType as string | undefined;
                    setAddEditDialogState({
                      ...defaultAddEditDocumentDialogState,
                      open: true,
                      mode: EditMode.ADD,
                      documentType: (issueDocType as DocumentType) ?? undefined,
                      currentSubmissionDocument: true,
                      facilities: data?.facility
                        ? [
                            {
                              id: data.facility.id,
                              name: data.facility.name,
                            },
                          ]
                        : undefined,
                    });
                  }}
                  sx={{ whiteSpace: "nowrap", ml: 1 }}
                >
                  Add Document
                </Button>
              )}
            </Stack>
          );
        }
        const showIcon = isStaff && isDefaultSubmissionDoc(row);
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            {showIcon ? (
              <Tooltip title={currentSubmissionTooltip}>
                <DriveFileMove fontSize="small" color="action" />
              </Tooltip>
            ) : isStaff ? (
              <Box sx={{ width: 20 }} />
            ) : null}
            <Typography variant="body2" noWrap>
              {prettyPrintDocumentType(row.documentType)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "title",
      headerName: "Name",
      flex: 1,
      renderCell({ row }) {
        if (isDocumentIssueRow(row)) return null;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <FileTypeIcon ext={row.fileExtension} />
            <Typography variant="body2" noWrap>
              {row.title}
            </Typography>
          </Stack>
        );
      },
    },
    reportingYearsColumn,
    {
      field: "createdAt",
      headerName: "Uploaded",
      width: 120,
      renderCell({ row }) {
        if (isDocumentIssueRow(row)) return null;
        return (
          <Typography variant="body2" noWrap>
            {prettyPrintDateMed(row.createdAt)}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      width: 120,
      getActions: ({ row }) => {
        if (isDocumentIssueRow(row)) return []; // covered by colSpan
        const actions = [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            disabled={isReadOnly}
            onClick={() => openEditDialog(row)}
          />,
          <GridActionsCellItem
            key="download"
            icon={<Download />}
            label="Download"
            onClick={async () => await fetchAndHandleDownload({ documentId: row.id })}
          />,
        ];

        if (isStaff && KnownSubmissionDocumentTypes.includes(row.documentType as DocumentType)) {
          if (row.facilityDocument?.currentSubmissionDocument === true) {
            actions.push(
              <GridActionsCellItem
                key="unset-current"
                icon={<DriveFileMoveOutlined sx={{ color: theme.palette.secondary.main }} />}
                label={
                  <span
                    style={{
                      color: theme.palette.secondary.main,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Remove as current submission doc
                  </span>
                }
                showInMenu
                disabled={isReadOnly}
                onClick={() => handleSetCurrent(row, false)}
              />
            );
          } else {
            actions.push(
              <GridActionsCellItem
                key="set-current"
                icon={<DriveFileMove sx={{ color: theme.palette.secondary.main }} />}
                label={
                  <span
                    style={{
                      color: theme.palette.secondary.main,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Set as current submission doc
                  </span>
                }
                showInMenu
                disabled={isReadOnly}
                onClick={() => handleSetCurrent(row, true)}
              />
            );
          }
        }

        actions.push(
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            showInMenu
            disabled={isReadOnly}
            onClick={() => {
              setDocumentToRemove(row);
              setConfirmDialogOpen(true);
            }}
          />
        );

        return actions;
      },
    },
  ];

  const reportDocRows: DataGridRow[] = useMemo(() => reportDocuments, [reportDocuments]);

  const pinnedIssueRows = useMemo(
    () => ({
      bottom: [
        ...reportDocumentIssues.map((i) => ({
          ...i,
          id: i.message,
        })),
        ...staffWarnings,
      ] as DataGridRow[],
    }),
    [reportDocumentIssues, staffWarnings]
  );

  type OtherDocRow = OtherFacilityDocumentsQuery["documents"]["items"][0];

  const otherDocColumns: OmnisearchGridColDef<OtherDocRow>[] = useMemo(
    () => [
      {
        field: "documentType",
        headerName: "Type",
        width: 300,
        filterKeyType: "enumAutocomplete" as const,
        renderCell({ row }) {
          const showIcon = isStaff && isDefaultSubmissionDoc(row);
          return (
            <Stack direction="row" alignItems="center" gap={1}>
              {showIcon ? (
                <Tooltip title={currentSubmissionTooltip}>
                  <DriveFileMove fontSize="small" color="action" />
                </Tooltip>
              ) : isStaff ? (
                <Box sx={{ width: 20 }} />
              ) : null}
              <Typography variant="body2" noWrap>
                {prettyPrintDocumentType(row.documentType)}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "title",
        headerName: "Name",
        flex: 1,
        renderCell({ row }) {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <FileTypeIcon ext={row.fileExtension} />
              <Typography variant="body2" noWrap>
                {row.title}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "associatedReports",
        headerName: "Reporting Years",
        width: 220,
        sortable: false,
        renderCell({ row }) {
          const reports = row.associatedReports ?? [];
          const epcraYears = [
            ...new Set(
              reports.filter((r) => r.programArea === "EPCRA").map((r) => r.reportingYear)
            ),
          ].sort((a, b) => b - a);

          if (epcraYears.length === 0) return null;

          const visible = epcraYears.slice(0, MAX_VISIBLE_YEARS);
          const overflow = epcraYears.length - MAX_VISIBLE_YEARS;

          return (
            <Stack direction="row" flexWrap="nowrap" gap={0.5} overflow="hidden">
              {visible.map((year) => (
                <Chip key={year} label={year} size="small" color="primary" variant="outlined" />
              ))}
              {overflow > 0 && (
                <Tooltip title={epcraYears.slice(MAX_VISIBLE_YEARS).join(", ")}>
                  <Chip label={`+${overflow}`} size="small" variant="outlined" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        width: 120,
        renderCell({ row }) {
          return (
            <Typography variant="body2" noWrap>
              {prettyPrintDateMed(row.createdAt)}
            </Typography>
          );
        },
      },
      ...(hasSemanticResults && isStaff
        ? [
            {
              field: "similarity",
              headerName: "Relevance",
              width: 120,
              sortable: false,
              filterable: false,
              resizable: false,
              valueGetter(_: unknown, row: OtherDocRow) {
                const similarity = similarityMapRef.current?.get(row.id);
                return similarity != null ? Math.round(similarity * 100) : null;
              },
              renderCell(params) {
                return <SimilarityCell value={params.value as number | null} />;
              },
            } as OmnisearchGridColDef<OtherDocRow>,
          ]
        : []),
      {
        field: "actions",
        type: "actions",
        width: 120,
        getActions: ({ row }: { row: OtherDocRow }) => {
          const actions = [
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label="Edit"
              disabled={isReadOnly}
              onClick={() => openEditDialog(row)}
            />,
            <GridActionsCellItem
              key="download"
              icon={<Download />}
              label="Download"
              onClick={async () => await fetchAndHandleDownload({ documentId: row.id })}
            />,
          ];
          if (isStaff && KnownSubmissionDocumentTypes.includes(row.documentType as DocumentType)) {
            actions.push(
              <GridActionsCellItem
                key="set-current"
                icon={<DriveFileMove sx={{ color: theme.palette.secondary.main }} />}
                label={
                  <span
                    style={{
                      color: theme.palette.secondary.main,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Set as current submission doc
                  </span>
                }
                showInMenu
                disabled={isReadOnly}
                onClick={() => handleSetCurrent(row, true)}
              />
            );
          }
          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              showInMenu
              disabled={isReadOnly}
              onClick={() => {
                setDocumentToRemove(row);
                setConfirmDialogOpen(true);
              }}
            />
          );
          return actions;
        },
      },
    ],
    // oxlint-disable-next-line exhaustive-deps
    [isReadOnly, isStaff, hasSemanticResults, theme.palette.secondary.main]
  );

  const getOtherDocItems = useCallback((data: OtherFacilityDocumentsQuery) => {
    return data.documents.items;
  }, []);

  const getOtherDocCount = useCallback(
    (data: OtherFacilityDocumentsQuery) => data.documents.count ?? 0,
    []
  );

  return (
    <>
      <FacilityReadOnlyBanner facilityId={facilityId} />
      {/* Gray page body */}
      <Box
        sx={{
          background: theme.palette.surfaceSubtle,
          minHeight: "calc(100vh - 200px)",
          p: 2,
        }}
      >
        <Stack spacing={2}>
          {/* Section 1: Report Documents */}
          <Paper elevation={0} sx={{ px: 3, py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Report Documents
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              {reports.length > 0 ? (
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel id="report-selector-label">Report</InputLabel>
                  <Select
                    labelId="report-selector-label"
                    value={selectedReportId}
                    label="Report"
                    onChange={(e) => setSelectedReportId(e.target.value)}
                    renderValue={(value) => {
                      const r = reports.find((rpt) => rpt.id === value);
                      if (!r) return "";
                      return `${r.reportingYear} ${reportKindToLabel(r.reportKind, facilityState)}`;
                    }}
                  >
                    {reports.map((report) => (
                      <MenuItem key={report.id} value={report.id}>
                        <Stack>
                          <Typography variant="body2">
                            {report.reportingYear}{" "}
                            {reportKindToLabel(report.reportKind, facilityState)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            {statusToLabel(report.organizationStatus, report.encampStatus)}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No reports found for this facility.
                </Typography>
              )}
            </Stack>

            {selectedReport && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 0.5 }}
              >
                <Link
                  component={RouterLink}
                  to={`/o/${tenantId}/chemicals/reports/${selectedReport.id}`}
                  variant="body2"
                  underline="hover"
                >
                  {selectedReport.reportingYear}{" "}
                  {reportKindToLabel(selectedReport.reportKind, facilityState)} Report
                </Link>
                <Typography variant="body2" color="text.secondary">
                  {statusToLabel(selectedReport.organizationStatus, selectedReport.encampStatus)}
                </Typography>
                {selectedReport.verifiedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Verified {prettyPrintDateMed(selectedReport.verifiedAt)}
                    {selectedReport.verifierFullName
                      ? ` by ${selectedReport.verifierFullName}`
                      : ""}
                  </Typography>
                )}
                {selectedReport.submittedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Submitted {prettyPrintDateMed(selectedReport.submittedAt)}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <DownloadAllButton
                  reportId={selectedReportId}
                  tooltipTitle="Download all documents for this report as a zip"
                />
              </Stack>
            )}

            <DataGrid<DataGridRow>
              columns={buildColumns("report")}
              rows={reportDocRows}
              pinnedRows={pinnedIssueRows}
              initialState={{
                sorting: {
                  sortModel: [{ field: "documentType", sort: "asc" }],
                },
              }}
              loading={loading || reportDocsLoading}
              autoHeight
              hideFooter
              disableRowSelectionOnClick
              onRowClick={({ row }) => {
                if (!isDocumentIssueRow(row)) {
                  const facility = data?.facility;
                  const enriched = facility
                    ? { ...row, facilities: [{ id: facility.id, name: facility.name }] }
                    : row;
                  setDrawerDocument(enriched);
                }
              }}
              getRowClassName={(params) => (isDocumentIssueRow(params.row) ? "issue-row" : "")}
              sx={{
                "& .MuiDataGrid-menu .MuiListItemText-primary": {
                  textOverflow: "clip",
                  overflow: "visible",
                },
              }}
            />
          </Paper>

          {/* Section 2: All Facility Documents */}
          <Paper elevation={0} sx={{ px: 3, py: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              All Facility Documents
            </Typography>
            <OmnisearchDataGrid<
              OtherFacilityDocumentsQuery,
              OtherFacilityDocumentsQueryVariables,
              OtherDocRow
            >
              persistenceKey="chemical-facility-other-documents"
              commandButtons={[
                <CommandButton key="add" onClick={openAddDialog} disabled={isReadOnly}>
                  Add Document
                </CommandButton>,
              ]}
              columns={otherDocColumns}
              dataQuery={OTHER_FACILITY_DOCUMENTS}
              defaultSearch={`tenantId:${tenantId} facilityId:${facilityId} excludeRejectedSubmissionDocuments:true`}
              getItems={getOtherDocItems}
              getCount={getOtherDocCount}
              onDataStateChange={handleOtherDocsDataStateChange}
              initialSortModel={[{ field: "documentType", sort: "asc" }]}
              noDataMessage="No documents found for this facility."
              withPadding={false}
              disableRowSelectionOnClick
              onRowClick={({ row }) => setDrawerDocument(row)}
              excludeFilterColumns={["associatedReports", "actions"]}
              sx={{
                "& .MuiDataGrid-menu .MuiListItemText-primary": {
                  textOverflow: "clip",
                  overflow: "visible",
                },
              }}
            />
          </Paper>
        </Stack>
      </Box>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setDocumentToRemove(undefined);
        }}
        onConfirm={async () => {
          if (documentToRemove) {
            await deleteDocumentMutation({
              variables: { id: documentToRemove.id },
            });
          }
          setConfirmDialogOpen(false);
          setDocumentToRemove(undefined);
        }}
        header="Delete Document"
        info={`Are you sure you want to delete "${documentToRemove?.title}"? This action is permanent.`}
      />

      <ConfirmDialog
        open={!!fixCandidate}
        onClose={() => setFixCandidate(null)}
        onConfirm={fixCandidate?.action === "set-flag" ? handleFixSetFlag : handleFixAttach}
        header={
          fixCandidate?.issueType === "warning"
            ? "Set Current Submission Document"
            : "Fix Missing Document"
        }
        info={
          fixCandidate?.issueType === "warning" ? (
            <>
              <Typography sx={{ mb: 1.5 }}>
                &quot;{fixCandidate.document.title}&quot; is on this report but is not set as a
                Current Submission Document.
              </Typography>
              <Typography>Set it now so it will be copied to future reports?</Typography>
            </>
          ) : (
            (fixCandidate?.description ?? "")
          )
        }
        confirmText={fixCandidate?.issueType === "warning" ? "Yes, Set It" : undefined}
        actions={
          fixCandidate?.issueType === "error" && fixCandidate?.action === "attach" ? (
            <>
              <Button onClick={() => setFixCandidate(null)}>Cancel</Button>
              <Button onClick={handleFixUpload} disabled={isReadOnly}>
                Upload New
              </Button>
              <Button variant="contained" onClick={handleFixAttach} disabled={isReadOnly}>
                Attach to Report
              </Button>
            </>
          ) : undefined
        }
      />

      <DocumentDetailDrawer
        document={drawerDocument}
        open={!!drawerDocument}
        onClose={() => setDrawerDocument(undefined)}
        onEdit={
          isReadOnly
            ? undefined
            : (doc) => {
                setDrawerDocument(undefined);
                openEditDialog(doc);
              }
        }
        tenantId={tenantId}
        tenantName={tenantName}
      />

      {tenantId && (
        <DocumentDialog
          tenantId={tenantId}
          addEditDialogProps={{
            onSubmit: () => {},
            refetchQueries: [CHEMICAL_FACILITY_DOCUMENTS],
            dialogState: addEditDialogState,
            setDialogState: setAddEditDialogState,
            existingDefaults,
            reportId: selectedReportId,
            reportingYear: selectedReport?.reportingYear,
          }}
        />
      )}
    </>
  );
}
